/*
 * Mobile-only PDP bubble bar (≤749px) — collapses variant-picker + swatch-
 * picker into a row of summary bubbles. Tapping a bubble lifts the matching
 * picker into a CSS-driven bottom sheet (see bunny-mobile-pdp-bar.css).
 *
 * Bubble availability is detected from picker DOM at runtime so the bar
 * adapts to whatever the swatch-picker decided to render for this product.
 * Drawer DOM is the picker's existing tree — no DOM is moved.
 */

const SVG_CLOSE = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="6" y1="6" x2="18" y2="18"/><line x1="6" y1="18" x2="18" y2="6"/></svg>`;

// Auto-close on selection. Surfaces stay open so the customer can compare
// chips before committing.
const AUTO_CLOSE_TARGETS = new Set(['size', 'firmness', 'mode', 'signatures']);

// Fallback labels for fixed-bubble UI. Per-surface labels are discovered
// from picker region tabs at runtime (#discoverSurfaceBubbles).
const FALLBACK_LABELS = {
  size: { drawerTitle: 'Size', bubbleLabel: 'Size', emptyValue: '—' },
  firmness: { drawerTitle: 'Firmness', bubbleLabel: 'Firmness', emptyValue: '—' },
  signatures: { drawerTitle: 'Signature Classics', bubbleLabel: 'Signature', emptyValue: 'Browse' },
  premades: { drawerTitle: 'Pre-Mades', bubbleLabel: 'Pre-Made', emptyValue: 'Browse' },
  mode: { drawerTitle: 'Selection Mode', bubbleLabel: 'Mode', emptyValue: 'Entire', entireValue: 'Entire', splitValue: 'Split' },
};

class BunnyMobilePdpBar extends HTMLElement {
  #config = null;
  #variantPicker = null;
  #swatchPicker = null;
  // GOTCHA: picker's data-section-id can differ from config.sectionId when
  // the picker is a deeper-nested block where section.id resolves blank.
  // Trusting config.sectionId for radio-name lookups misses every signature
  // and mode radio. Source the scope from the picker we actually found.
  #swatchPickerScope = '';
  #section = null;
  #hostPanel = null;
  #backdrop = null;
  #activeTarget = null;
  #autoCloseTimer = null;

  // null if the option isn't on this product.
  #fieldsetIndex = { size: null, firmness: null };

  // Per-surface bubble registry: { key, isModel, surfaceName }. Built from
  // picker region tabs at init — no hardcoded surface names.
  #surfaceBubbles = [];
  #splitBubbles = [];
  #modelBubbleKey = null;

  #bubbles = new Map();
  #statusNameEl = null;
  #statusEl = null;
  #statusTextEl = null;
  #titleEl = null;
  #closeBtn = null;
  // Chrome relocates into the active picker on openDrawer (so absolute
  // top:0 tracks the drawer's actual height) and back into the bar on close.
  #chromeEl = null;
  #barInner = null;

  #onVariantUpdate = null;
  #onSwatchChange = null;
  #onRarityChange = null;
  #onSwatchPickerChange = null;
  #onVariantPickerChange = null;
  #onKeyDown = null;

  connectedCallback() {
    this.#config = this.#readConfig();
    if (!this.#config) {
      // Bad / missing config — render nothing rather than guess.
      return;
    }

    this.#section = this.closest('.shopify-section') || document.body;
    // Panel scope in the configurator stops sibling-panel events from
    // bleeding into this bar's bubbles. Falls back to section on PDP.
    this.#hostPanel = this.closest('[data-pour-panel]');
    const scope = this.#hostPanel || this.#section;
    this.#variantPicker = scope.querySelector(`variant-picker[data-section-id="${this.#config.sectionId}"]`)
      || scope.querySelector('variant-picker');
    this.#swatchPicker = scope.querySelector(`swatch-picker[data-section-id="${this.#config.sectionId}"]`)
      || scope.querySelector('swatch-picker');
    this.#swatchPickerScope = this.#swatchPicker?.dataset.sectionId ?? this.#config.sectionId;

    if (this.#variantPicker) this.#variantPicker.classList.add('bunny-mobile-drawer-target');
    if (this.#swatchPicker) this.#swatchPicker.classList.add('bunny-mobile-drawer-target');

    this.#detectVariantOptions();
    this.#discoverSurfaceBubbles();
    this.#render();
    this.#wireEvents();
    this.#hydrateInitialState();
  }

  disconnectedCallback() {
    this.#unwireEvents();
    if (this.#autoCloseTimer) {
      clearTimeout(this.#autoCloseTimer);
      this.#autoCloseTimer = null;
    }
  }

  #readConfig() {
    const script = this.querySelector('script[type="application/json"]');
    if (!script) return null;
    try {
      const parsed = JSON.parse(script.textContent || '{}');
      if (!parsed.sectionId) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  // Map Size / Firmness to their fieldset-index via legend text.
  #detectVariantOptions() {
    if (!this.#variantPicker) return;
    const fieldsets = this.#variantPicker.querySelectorAll('fieldset.variant-option');
    for (const fs of fieldsets) {
      const legend = fs.querySelector('legend');
      if (!legend) continue;
      // First text node is the option name (legend may also contain nested
      // option-swatch-value spans for swatch-style pickers).
      const name = legend.firstChild?.textContent?.trim().toLowerCase() || '';
      const idx = Number(fs.dataset.fieldsetIndex);
      if (Number.isNaN(idx)) continue;
      if (name === 'size') this.#fieldsetIndex.size = idx;
      else if (name === 'firmness') this.#fieldsetIndex.firmness = idx;
    }
  }

  // Build per-surface metadata from region tabs. Don't filter on [hidden]
  // — surface tabs hide per mode but bubbles for both modes must exist
  // so the mode bubble can flip them. Per-mode visibility lives in
  // #applyModeVisibility.
  #discoverSurfaceBubbles() {
    this.#surfaceBubbles = [];
    this.#splitBubbles = [];
    this.#modelBubbleKey = null;
    if (!this.#swatchPicker) return;
    const tabs = this.#swatchPicker.querySelectorAll('.swatch-picker__region-tab');
    for (const tab of tabs) {
      const key = tab.dataset.position;
      if (!key) continue;
      const isModel = tab.dataset.isModel === 'true';
      const surfaceName = tab.dataset.surfaceName || key;
      const entry = { key, isModel, surfaceName };
      this.#surfaceBubbles.push(entry);
      if (isModel && !this.#modelBubbleKey) this.#modelBubbleKey = key;
      else if (!isModel) this.#splitBubbles.push(entry);
    }
  }

  // Server config overrides via labels[key]; otherwise derive from surface_name.
  #labelForSurface(entry) {
    const fromConfig = this.#config?.labels?.[entry.key];
    if (fromConfig) return { drawerTitle: 'Pick a color', bubbleLabel: entry.surfaceName, emptyValue: 'Pick a color', ...fromConfig };
    return {
      drawerTitle: `${entry.surfaceName} Color`,
      bubbleLabel: entry.surfaceName,
      emptyValue: 'Pick a color',
    };
  }

  // True iff the mode toggle (Entire / Split) is rendered — only present
  // when the variant has Model + ≥1 non-Model surface.
  #hasModeToggle() {
    return Boolean(this.#swatchPicker?.querySelector('.swatch-picker__mode-row'));
  }

  #hasTabPanel(tab) {
    return Boolean(
      this.#swatchPicker?.querySelector(`.swatch-picker__tab-panel[data-tab="${tab}"]`)
    );
  }

  #render() {
    const cfg = this.#config;
    const labels = { ...FALLBACK_LABELS, ...(cfg.labels || {}) };

    const inner = document.createElement('div');
    inner.className = 'bunny-mobile-pdp-bar__inner';

    // Status row (Pour Style + rarity readout). Only when there's a
    // swatch-picker; standalone variant-picker products (no customizer)
    // shouldn't show a Pour Style readout.
    if (this.#swatchPicker) {
      // Rarity badge. The Signature Classic name is the FIRST item inside it
      // (matching the desktop pour-style block); the rarity text lives in a
      // sibling span so the name survives the textContent writes in
      // #updateStatus. data-rarity (colour + flanking dots) stays on the badge.
      const status = document.createElement('div');
      status.className = 'bunny-mobile-pdp-status';
      status.dataset.empty = cfg.statusEmpty || 'Pick swatches to begin';

      const statusName = document.createElement('span');
      statusName.className = 'bunny-mobile-pdp-status-name';
      statusName.hidden = true;
      status.appendChild(statusName);
      this.#statusNameEl = statusName;

      const statusText = document.createElement('span');
      statusText.className = 'bunny-mobile-pdp-status-text';
      statusText.textContent = status.dataset.empty;
      status.appendChild(statusText);
      this.#statusTextEl = statusText;

      inner.appendChild(status);
      this.#statusEl = status;
    }

    // Bubble row.
    const bubbles = document.createElement('div');
    bubbles.className = 'bunny-mobile-pdp-bar__bubbles';

    // Bubble availability — driven by what the pickers actually rendered.
    // Order: size, firmness, mode (toggle), signatures (tab), then one
    // bubble per discovered surface (Model first, then split surfaces in
    // render order), then premades (tab).
    const wantBubbles = [];
    if (this.#fieldsetIndex.size !== null) wantBubbles.push({ target: 'size', label: labels.size });
    if (this.#fieldsetIndex.firmness !== null) wantBubbles.push({ target: 'firmness', label: labels.firmness });
    if (this.#hasModeToggle()) wantBubbles.push({ target: 'mode', label: labels.mode });
    if (this.#hasTabPanel('signatures')) wantBubbles.push({ target: 'signatures', label: labels.signatures });
    // Model bubble first, then split surfaces.
    const orderedSurfaces = [];
    const modelEntry = this.#surfaceBubbles.find((e) => e.isModel);
    if (modelEntry) orderedSurfaces.push(modelEntry);
    for (const e of this.#splitBubbles) orderedSurfaces.push(e);
    for (const entry of orderedSurfaces) {
      wantBubbles.push({ target: entry.key, label: this.#labelForSurface(entry), isSurface: true });
    }
    if (this.#hasTabPanel('premades')) wantBubbles.push({ target: 'premades', label: labels.premades });

    for (const { target, label, isSurface } of wantBubbles) {
      const btn = this.#buildBubble(target, label, isSurface === true);
      bubbles.appendChild(btn);
      this.#bubbles.set(target, btn);
    }

    inner.appendChild(bubbles);

    // Drawer chrome — hidden until a drawer opens.
    const chrome = document.createElement('div');
    chrome.className = 'bunny-mobile-pdp-bar__chrome';

    const title = document.createElement('h2');
    title.className = 'bunny-mobile-pdp-bar__title';
    chrome.appendChild(title);
    this.#titleEl = title;

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'bunny-mobile-pdp-bar__close';
    close.setAttribute('aria-label', cfg.closeLabel || 'Close');
    close.innerHTML = SVG_CLOSE;
    close.addEventListener('click', () => this.closeDrawer());
    chrome.appendChild(close);
    this.#closeBtn = close;

    inner.appendChild(chrome);
    this.#chromeEl = chrome;
    this.#barInner = inner;

    this.appendChild(inner);

    // Bail out if there are no bubbles to show — nothing to anchor on.
    if (this.#bubbles.size === 0) {
      this.style.display = 'none';
    }
  }

  // Build a single bubble. Shape language: a swatch-style chip square
  // Surface bubbles get a cloned .swatch-chip__visual; text bubbles get
  // a short value (Size / Firmness / Mode / Signature / Pre-Made).
  #buildBubble(target, label, isSwatchBubble = false) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'bunny-mobile-pdp-bubble';
    btn.dataset.target = target;
    btn.setAttribute('aria-haspopup', 'dialog');

    const visual = document.createElement('span');
    visual.className = 'bunny-mobile-pdp-bubble__visual';
    visual.dataset.key = 'visual';

    // Bubbles start in `--empty` (dashed-border); first real value clears it.
    visual.classList.add('bunny-mobile-pdp-bubble__visual--empty');

    if (isSwatchBubble) {
      // Placeholder text; #paintBubbleChip swaps in a cloned chip visual.
      visual.textContent = label.emptyValue;
    } else {
      // Inner span lets #setBubbleValue update without disturbing the shell.
      const text = document.createElement('span');
      text.className = 'bunny-mobile-pdp-bubble__text';
      text.dataset.key = 'value';
      text.textContent = label.emptyValue;
      visual.appendChild(text);
    }

    btn.appendChild(visual);

    const caption = document.createElement('span');
    caption.className = 'bunny-mobile-pdp-bubble__caption';
    caption.textContent = label.bubbleLabel;
    btn.appendChild(caption);

    btn.addEventListener('click', () => this.openDrawer(target));

    return btn;
  }

  #wireEvents() {
    // Horizon's morph copyAttributes wholesale-replaces class + data-* on
    // matched elements — our markers get stripped (they're not in the
    // server HTML). Defer a frame for the morph to settle, then re-apply.
    this.#onVariantUpdate = () => {
      requestAnimationFrame(() => {
        this.#reattachPickerMarkers();
        this.#detectVariantOptions();
        this.#updateVariantBubblesFromPicker();
      });
    };
    document.addEventListener('variant:update', this.#onVariantUpdate);

    // bunny:swatch-change carries detail.mode — canonical source for the
    // mode bubble. The picker's #applyMode writes input.checked without
    // firing 'change', so we can't catch cascade-driven mode flips otherwise.
    // See memory `swatch_picker_apply_mode_no_change_event.md`.
    this.#onSwatchChange = (event) => {
      if (!this.#isFromOurSection(event)) return;
      const detail = event.detail;
      if (!detail || !detail.region) return;
      if (detail.mode) {
        this.#updateModeBubble(detail.mode);
        this.#applyModeVisibility(detail.mode);
      }
      this.#updateRegionBubble(detail.region, detail.swatch);
      // Picker calls #syncSignaturePillForSelection synchronously before
      // emit, so by now any non-matching signature is already unchecked
      // (programmatically — no 'change' fires). Poll the radios here.
      this.#syncSignatureBubble();
    };
    document.addEventListener('bunny:swatch-change', this.#onSwatchChange);

    // bunny:rarity-change — drives the status row.
    this.#onRarityChange = (event) => {
      if (!this.#isFromOurSection(event)) return;
      this.#updateStatus(event.detail);
    };
    document.addEventListener('bunny:rarity-change', this.#onRarityChange);

    // Swatch-picker change (mode / signature radios) — auto-close drawers.
    if (this.#swatchPicker) {
      this.#onSwatchPickerChange = (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        const scope = this.#swatchPickerScope;
        if (target.name === `swatch-picker-mode-${scope}` && target.checked) {
          this.#updateModeBubble(target.value);
          this.#applyModeVisibility(target.value);
          if (this.#activeTarget === 'mode') this.#scheduleAutoClose();
        }
        if (target.name === `swatch-picker-signature-${scope}` && target.checked) {
          this.#updateSignatureBubble(target);
          if (this.#activeTarget === 'signatures') this.#scheduleAutoClose();
        }
      };
      this.#swatchPicker.addEventListener('change', this.#onSwatchPickerChange);
    }

    // Size/Firmness radio click → auto-close once the morph settles.
    if (this.#variantPicker) {
      this.#onVariantPickerChange = (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (target.type !== 'radio') return;
        if (this.#activeTarget === 'size' || this.#activeTarget === 'firmness') {
          this.#scheduleAutoClose();
        }
      };
      this.#variantPicker.addEventListener('change', this.#onVariantPickerChange);
    }

    this.#onKeyDown = (event) => {
      if (event.key === 'Escape' && this.#activeTarget) {
        event.preventDefault();
        this.closeDrawer();
      }
    };
    document.addEventListener('keydown', this.#onKeyDown);
  }

  #unwireEvents() {
    if (this.#onVariantUpdate) document.removeEventListener('variant:update', this.#onVariantUpdate);
    if (this.#onSwatchChange) document.removeEventListener('bunny:swatch-change', this.#onSwatchChange);
    if (this.#onRarityChange) document.removeEventListener('bunny:rarity-change', this.#onRarityChange);
    if (this.#onKeyDown) document.removeEventListener('keydown', this.#onKeyDown);
    if (this.#swatchPicker && this.#onSwatchPickerChange) {
      this.#swatchPicker.removeEventListener('change', this.#onSwatchPickerChange);
    }
    if (this.#variantPicker && this.#onVariantPickerChange) {
      this.#variantPicker.removeEventListener('change', this.#onVariantPickerChange);
    }
  }

  // Re-apply markers + drawer-open state stripped by the morph's copyAttributes.
  #reattachPickerMarkers() {
    if (this.#variantPicker) {
      this.#variantPicker.classList.add('bunny-mobile-drawer-target');
    }
    if (this.#swatchPicker) {
      this.#swatchPicker.classList.add('bunny-mobile-drawer-target');
    }
    if (!this.#activeTarget) return;
    if (this.#activeTarget === 'size' || this.#activeTarget === 'firmness') {
      this.#activateVariantPicker(this.#activeTarget);
    } else {
      this.#activateSwatchPicker(this.#activeTarget);
    }
  }

  // Scope bunny:* events to our panel (configurator) or section (PDP).
  #isFromOurSection(event) {
    const scope = this.#hostPanel || this.#section;
    if (!scope) return true;
    const target = event.target;
    if (!(target instanceof Node)) return true;
    return scope.contains(target);
  }

  // Pick up state already set by the pickers before the bar mounted.
  #hydrateInitialState() {
    this.#updateVariantBubblesFromPicker();
    this.#hydrateSwatchBubblesFromPicker();
    this.#hydrateModeBubble();
    this.#hydrateSignatureBubble();
  }

  #updateVariantBubblesFromPicker() {
    if (!this.#variantPicker) return;
    if (this.#fieldsetIndex.size !== null) {
      const sizeBubble = this.#bubbles.get('size');
      const value = this.#readVariantOptionValue(this.#fieldsetIndex.size);
      if (sizeBubble && value) this.#setBubbleValue(sizeBubble, value);
    }
    if (this.#fieldsetIndex.firmness !== null) {
      const firmnessBubble = this.#bubbles.get('firmness');
      const value = this.#readVariantOptionValue(this.#fieldsetIndex.firmness);
      if (firmnessBubble && value) this.#setBubbleValue(firmnessBubble, value);
    }
  }

  // Fallback to data-current-checked covers the brief moment before
  // Horizon writes the `checked` attribute.
  #readVariantOptionValue(fieldsetIndex) {
    if (!this.#variantPicker) return null;
    const fieldset = this.#variantPicker.querySelector(
      `fieldset[data-fieldset-index="${fieldsetIndex}"]`
    );
    if (!fieldset) return null;
    const checked = fieldset.querySelector('input[type="radio"]:checked')
      || fieldset.querySelector('input[data-current-checked="true"]');
    return checked?.value || null;
  }

  #hydrateSwatchBubblesFromPicker() {
    if (!this.#swatchPicker) return;
    for (const entry of this.#surfaceBubbles) {
      const bubble = this.#bubbles.get(entry.key);
      if (!bubble) continue;
      const chip = this.#swatchPicker.querySelector(
        `.swatch-chip[data-position="${entry.key}"][aria-pressed="true"]`
      );
      if (chip) {
        this.#paintBubbleChip(bubble, chip);
        const label = chip.dataset.label || '';
        if (label) this.#setBubbleValue(bubble, label);
      }
    }
  }

  #hydrateModeBubble() {
    if (!this.#swatchPicker || !this.#bubbles.has('mode')) return;
    const checked = this.#swatchPicker.querySelector(
      `input[name="swatch-picker-mode-${this.#swatchPickerScope}"]:checked`
    );
    if (checked) {
      this.#updateModeBubble(checked.value);
      this.#applyModeVisibility(checked.value);
    } else {
      const initial = this.#swatchPicker.dataset.initialMode || 'split';
      this.#updateModeBubble(initial);
      this.#applyModeVisibility(initial);
    }
  }

  #hydrateSignatureBubble() {
    if (!this.#swatchPicker || !this.#bubbles.has('signatures')) return;
    const checked = this.#swatchPicker.querySelector(
      `input[name="swatch-picker-signature-${this.#swatchPickerScope}"]:checked`
    );
    if (checked) this.#updateSignatureBubble(checked);
  }

  #updateRegionBubble(region, swatch) {
    const bubble = this.#bubbles.get(region);
    if (!bubble || !swatch) return;
    if (swatch.label) this.#setBubbleValue(bubble, swatch.label);
    if (!this.#swatchPicker) return;
    const chip = this.#swatchPicker.querySelector(
      `.swatch-chip[data-position="${region}"][data-swatch-id="${swatch.id}"]`
    );
    if (chip) this.#paintBubbleChip(bubble, chip);
  }

  // Clone the picked chip's .swatch-chip__visual into the bubble; clear --empty.
  #paintBubbleChip(bubble, sourceChip) {
    const slot = bubble.querySelector('[data-key="visual"]');
    if (!slot) return;
    slot.classList.remove('bunny-mobile-pdp-bubble__visual--empty');
    slot.replaceChildren();
    const visual = sourceChip.querySelector('.swatch-chip__visual');
    if (visual) slot.appendChild(visual.cloneNode(true));
    this.#reflectSparkleOnBubble(slot, sourceChip.dataset.position);
  }

  // Mirror a surface's Sparkle toggle onto the bubble slot. The swatch-picker
  // sparkle overlay (swatch-picker.css) is gated by --sparkle-* custom props;
  // the cloned bubble visual sits outside the picker's :has() reach, so we set
  // those props here. Reads the live checkbox (kept in sync by the picker), so
  // it's correct on both swatch picks and standalone Sparkle toggles — both
  // re-run this via the bunny:swatch-change → #updateRegionBubble path.
  #reflectSparkleOnBubble(slot, region) {
    if (!slot || !region) return;
    const on = !!this.#swatchPicker?.querySelector(
      `.swatch-picker__sparkle-input[data-position="${region}"]`
    )?.checked;
    if (on) {
      slot.style.setProperty('--sparkle-a', 'swatch-chip-sparkle-a');
      slot.style.setProperty('--sparkle-b', 'swatch-chip-sparkle-b');
      slot.style.setProperty('--sparkle-op', '0.6');
    } else {
      slot.style.removeProperty('--sparkle-a');
      slot.style.removeProperty('--sparkle-b');
      slot.style.removeProperty('--sparkle-op');
    }
  }

  // Drop --empty once a real value lands.
  #setBubbleValue(bubble, value) {
    const visual = bubble.querySelector('[data-key="visual"]');
    if (visual) visual.classList.remove('bunny-mobile-pdp-bubble__visual--empty');
    const valueEl = bubble.querySelector('[data-key="value"]');
    if (valueEl) {
      valueEl.textContent = value;
      return;
    }
    // Swatch bubble in empty state — value text in the visual itself.
    // (Brief intermediate state before #paintBubbleChip lands.)
    if (visual) visual.textContent = value;
  }

  #updateModeBubble(mode) {
    const bubble = this.#bubbles.get('mode');
    if (!bubble) return;
    const cfg = this.#config;
    const labels = { ...FALLBACK_LABELS, ...(cfg.labels || {}) };
    const label = mode === 'entire'
      ? (labels.mode.entireValue || 'Entire')
      : (labels.mode.splitValue || 'Split');
    this.#setBubbleValue(bubble, label);
  }

  // Poll signature radios after bunny:swatch-change — picker may have
  // unticked programmatically with no 'change' event.
  #syncSignatureBubble() {
    if (!this.#bubbles.has('signatures')) return;
    if (!this.#swatchPicker) return;
    const checked = this.#swatchPicker.querySelector(
      `input[name="swatch-picker-signature-${this.#swatchPickerScope}"]:checked`
    );
    if (checked) {
      this.#updateSignatureBubble(checked);
    } else {
      this.#resetSignatureBubble();
    }
  }

  // Restore empty placeholder state — re-create the inner __text span so
  // future #setBubbleValue calls find it.
  #resetSignatureBubble() {
    const bubble = this.#bubbles.get('signatures');
    if (!bubble) return;
    const visual = bubble.querySelector('[data-key="visual"]');
    if (!visual) return;
    const labels = { ...FALLBACK_LABELS, ...(this.#config.labels || {}) };
    const empty = labels.signatures?.emptyValue || 'Browse';
    visual.classList.add('bunny-mobile-pdp-bubble__visual--empty');
    const text = document.createElement('span');
    text.className = 'bunny-mobile-pdp-bubble__text';
    text.dataset.key = 'value';
    text.textContent = empty;
    visual.replaceChildren(text);
  }

  // Clone the picker's .swatch-picker__signature-chips into the bubble.
  // Falls back to signature name text if the chip pair can't be found.
  #updateSignatureBubble(input) {
    const bubble = this.#bubbles.get('signatures');
    if (!bubble) return;
    const visual = bubble.querySelector('[data-key="visual"]');
    if (!visual) return;

    const sigLabel = input.closest('.swatch-picker__signature');
    const chips = sigLabel?.querySelector('.swatch-picker__signature-chips');

    if (chips) {
      visual.classList.remove('bunny-mobile-pdp-bubble__visual--empty');
      visual.replaceChildren(chips.cloneNode(true));
      return;
    }

    const sigId = input.value;
    const sigName = this.#config.signatureNames?.[sigId] || '';
    if (sigName) this.#setBubbleValue(bubble, sigName);
  }

  // Entire → Model only; Split → non-Model only.
  #applyModeVisibility(mode) {
    if (this.#modelBubbleKey) {
      const modelBubble = this.#bubbles.get(this.#modelBubbleKey);
      if (modelBubble) modelBubble.hidden = mode !== 'entire';
    }
    for (const entry of this.#splitBubbles) {
      const bubble = this.#bubbles.get(entry.key);
      if (bubble) bubble.hidden = mode !== 'split';
    }
  }

  #updateStatus(detail) {
    if (!this.#statusEl) return;
    // Rarity text goes in the sibling text span; the signature name is the
    // first child of the badge. Both only show when a combo is resolved (has a
    // cost). data-rarity stays on the badge container (colour + dots).
    const textEl = this.#statusTextEl || this.#statusEl;
    const signatureName = detail && detail.cost ? detail.signatureName || '' : '';
    if (this.#statusNameEl) {
      this.#statusNameEl.textContent = signatureName;
      this.#statusNameEl.hidden = !signatureName;
    }
    if (!detail || !detail.cost) {
      textEl.textContent = this.#statusEl.dataset.empty || '';
      delete this.#statusEl.dataset.rarity;
      return;
    }
    // Show only the Pour Style name; the rarity tier word is dropped
    // (data-rarity still drives the badge colour + flanking dots).
    textEl.textContent = detail.pourStyle || (this.#statusEl.dataset.empty || '');
    this.#statusEl.dataset.rarity = String(detail.cost);
  }

  // ── Drawer open / close ─────────────────────────────────────

  openDrawer(target) {
    if (!target) return;
    if (this.#activeTarget === target) {
      this.closeDrawer();
      return;
    }
    if (this.#activeTarget) this.#deactivateCurrent();

    this.#activeTarget = target;
    this.dataset.mobileDrawer = target;
    document.body.dataset.mobileDrawerOpen = target;
    this.#ensureBackdrop();

    // Surface bubbles derive labels from picker tabs; fixed bubbles use
    // FALLBACK_LABELS overlaid with server config.
    const fixedLabels = { ...FALLBACK_LABELS, ...(this.#config.labels || {}) };
    const surfaceEntry = this.#surfaceBubbles.find((e) => e.key === target);
    const targetLabel = surfaceEntry
      ? this.#labelForSurface(surfaceEntry)
      : fixedLabels[target];
    if (this.#titleEl) {
      this.#titleEl.textContent = targetLabel?.drawerTitle || '';
    }

    // Pickers — mark the right one active and route the data attribute.
    if (target === 'size' || target === 'firmness') {
      this.#activateVariantPicker(target);
    } else {
      this.#activateSwatchPicker(target);
    }

    // Focus the close button so Escape works + screen readers announce.
    requestAnimationFrame(() => {
      this.#closeBtn?.focus({ preventScroll: true });
    });
  }

  closeDrawer() {
    if (!this.#activeTarget) return;
    this.#deactivateCurrent();
    this.#activeTarget = null;
    delete this.dataset.mobileDrawer;
    delete document.body.dataset.mobileDrawerOpen;
    if (this.#autoCloseTimer) {
      clearTimeout(this.#autoCloseTimer);
      this.#autoCloseTimer = null;
    }
  }

  #scheduleAutoClose() {
    if (!this.#activeTarget) return;
    if (!AUTO_CLOSE_TARGETS.has(this.#activeTarget)) return;
    if (this.#autoCloseTimer) clearTimeout(this.#autoCloseTimer);
    // Brief delay so the customer sees the selection settle.
    this.#autoCloseTimer = setTimeout(() => {
      this.#autoCloseTimer = null;
      this.closeDrawer();
    }, 220);
  }

  #activateVariantPicker(target) {
    if (!this.#variantPicker) return;
    const idx = this.#fieldsetIndex[target];
    if (typeof idx !== 'number') return;
    for (const fs of this.#variantPicker.querySelectorAll('fieldset.variant-option')) {
      fs.classList.toggle('bunny-mobile-active-fieldset',
        fs.dataset.fieldsetIndex === String(idx));
    }
    if (this.#chromeEl) this.#variantPicker.prepend(this.#chromeEl);
    this.#variantPicker.classList.add('bunny-mobile-drawer-active');
    this.#variantPicker.dataset.mobileDrawerOpen = target;
  }

  #activateSwatchPicker(target) {
    if (!this.#swatchPicker) return;
    if (this.#chromeEl) this.#swatchPicker.prepend(this.#chromeEl);
    this.#swatchPicker.classList.add('bunny-mobile-drawer-active');
    this.#swatchPicker.dataset.mobileDrawerOpen = target;
    // Surface drawers: also nudge the picker's #activeRegion so its
    // preview slots stay in sync if the customer goes back to desktop.
    const isSurfaceTarget = this.#surfaceBubbles.some((e) => e.key === target);
    if (isSurfaceTarget) {
      // Markers drive CSS rules that scope visibility to the open surface
      // — no per-handle-name CSS, so new surfaces work without code changes.
      this.#swatchPicker.dataset.mobileSurfaceDrawer = 'true';
      for (const group of this.#swatchPicker.querySelectorAll('.swatch-picker__group')) {
        group.classList.toggle(
          'bunny-mobile-active-group',
          group.dataset.position === target
        );
      }
      const tab = this.#swatchPicker.querySelector(
        `.swatch-picker__region-tab[data-position="${target}"]`
      );
      if (tab && tab.getAttribute('aria-selected') !== 'true') {
        tab.click();
      }
    }
  }

  #deactivateCurrent() {
    if (this.#variantPicker) {
      this.#variantPicker.classList.remove('bunny-mobile-drawer-active');
      delete this.#variantPicker.dataset.mobileDrawerOpen;
      for (const fs of this.#variantPicker.querySelectorAll('fieldset.bunny-mobile-active-fieldset')) {
        fs.classList.remove('bunny-mobile-active-fieldset');
      }
    }
    if (this.#swatchPicker) {
      this.#swatchPicker.classList.remove('bunny-mobile-drawer-active');
      delete this.#swatchPicker.dataset.mobileDrawerOpen;
      delete this.#swatchPicker.dataset.mobileSurfaceDrawer;
      for (const group of this.#swatchPicker.querySelectorAll('.swatch-picker__group.bunny-mobile-active-group')) {
        group.classList.remove('bunny-mobile-active-group');
      }
    }
    if (this.#chromeEl && this.#barInner && this.#chromeEl.parentNode !== this.#barInner) {
      this.#barInner.appendChild(this.#chromeEl);
    }
  }

  #ensureBackdrop() {
    if (this.#backdrop && this.#backdrop.isConnected) return;
    let el = document.querySelector('.bunny-mobile-pdp-backdrop');
    if (!el) {
      el = document.createElement('div');
      el.className = 'bunny-mobile-pdp-backdrop';
      el.addEventListener('click', () => this.closeDrawer());
      document.body.appendChild(el);
    }
    this.#backdrop = el;
  }
}

if (!customElements.get('bunny-mobile-pdp-bar')) {
  customElements.define('bunny-mobile-pdp-bar', BunnyMobilePdpBar);
}
